import React from 'react';
import { ArrowLeft, Save, X, ChevronRight, Trash2 } from 'lucide-react';
import { InvoiceRecord } from '@types';
import { StatusOverride } from '@molecules/StatusOverride';

// Helper function to check if assignment is required for next step
const isAssignmentRequiredForNextStep = (
  currentStatus: string,
  currentUserId?: string,
  assignedToUserId?: string
): boolean => {
  // Mandatory assignment statuses (Step 2, 4, 5, 8)
  const mandatoryAssignmentStatuses = ['adding', 'ir', 'invoicing', 'entering_stock'];

  if (mandatoryAssignmentStatuses.includes(currentStatus)) {
    // User must be assigned to the invoice to proceed to next step
    return !(currentUserId && assignedToUserId && currentUserId === assignedToUserId);
  }

  return false;
};

interface InvoiceHeaderProps {
  invoice: InvoiceRecord;
  currentStatus: string;
  isLoading: boolean;
  statusDisplayName: string;
  nextStatus: any;
  nextStepButtonText: string;
  hasOverrideRole: boolean;
  canDeleteInvoice: boolean;
  currentUserId?: string;
  assignedToUserId?: string;
  onCancel: () => void;
  onSave: () => void;
  onStatusUpdate: () => void;
  onStatusOverride: (newStatus: string) => Promise<void>;
  onDelete: () => void;
  updateStatusMutationPending: boolean;
  unassignMutationPending: boolean;
  deleteInvoiceMutationPending: boolean;
  formData: any;
}

export const InvoiceHeader: React.FC<InvoiceHeaderProps> = ({
  invoice,
  currentStatus,
  isLoading,
  statusDisplayName,
  nextStatus,
  nextStepButtonText,
  hasOverrideRole,
  canDeleteInvoice,
  currentUserId,
  assignedToUserId,
  onCancel,
  onSave,
  onStatusUpdate,
  onStatusOverride,
  onDelete,
  updateStatusMutationPending,
  unassignMutationPending,
  deleteInvoiceMutationPending,
  formData
}) => {
  return (
    <div className="bg-white shadow-sm border-b border-secondary-200 flex-shrink-0">
      <div className="px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={onCancel}
              className="inline-flex items-center px-3 py-2 border border-secondary-300 rounded-md text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-secondary-900">
                Edit Invoice ({statusDisplayName})
              </h1>
              <p className="text-sm text-secondary-600">
                {invoice.supplier} - {invoice.invoice_number}
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="inline-flex items-center px-4 py-2 border border-secondary-300 rounded-md text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
            
            {nextStatus && (
              <button
                onClick={onStatusUpdate}
                disabled={
                  updateStatusMutationPending ||
                  unassignMutationPending ||
                  (currentStatus === 'new_products' && !formData.is_product_added) ||
                  isAssignmentRequiredForNextStep(currentStatus, currentUserId, assignedToUserId)
                }
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 disabled:opacity-50 transition-colors"
              >
                {(updateStatusMutationPending || unassignMutationPending) ? 'Updating...' : nextStepButtonText}
                {!(updateStatusMutationPending || unassignMutationPending) && <ChevronRight className="w-4 h-4 ml-2" />}
              </button>
            )}
            
            {hasOverrideRole && (
              <StatusOverride
                currentStatus={currentStatus}
                onStatusChange={onStatusOverride}
                isLoading={updateStatusMutationPending}
                disabled={isLoading}
              />
            )}
            
            {canDeleteInvoice && (
              <button
                onClick={onDelete}
                disabled={deleteInvoiceMutationPending}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleteInvoiceMutationPending ? 'Deleting...' : 'Delete'}
              </button>
            )}
            
            <button
              onClick={onSave}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4 mr-2" />
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
